import { type AppState, Auth0Context, type Auth0ContextInterface, Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { type FunctionComponent, type ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { setAccessToken } from 'src/lib/api/request';

interface Props {
  children: ReactNode;
}

const devUser = {
  isAuthenticated: true,
  isLoading: false,
  user: { sub: 'dev|local', name: 'Dev' },
  getAccessTokenSilently: async () => 'dev',
  loginWithRedirect: async () => undefined,
  logout: async () => undefined,
} as unknown as Auth0ContextInterface;

const SESSION_LOST = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'missing_refresh_token',
  'invalid_grant',
]);

const isSessionLost = (error: unknown) =>
  typeof error === 'object' && error !== null && SESSION_LOST.has(String((error as { error?: unknown }).error));

export const AccessToken: FunctionComponent = () => {
  const { isAuthenticated, getAccessTokenSilently, logout } = useAuth0();

  useEffect(() => {
    setAccessToken(
      isAuthenticated
        ? async () => {
            try {
              return await getAccessTokenSilently();
            } catch (error) {
              if (isSessionLost(error)) {
                await logout({ openUrl: false });
              }
              throw error;
            }
          }
        : null,
    );
  }, [isAuthenticated, getAccessTokenSilently, logout]);

  return null;
};

export const AuthProvider: FunctionComponent<Props> = ({ children }) => {
  const navigate = useNavigate();

  const onRedirectCallback = (state?: AppState) => {
    void navigate(state?.returnTo ?? '/bands', { replace: true });
  };

  if (import.meta.env.DEV && import.meta.env.VITE_AUTH_DISABLED === 'true') {
    return (
      <Auth0Context.Provider value={devUser}>
        <AccessToken />
        {children}
      </Auth0Context.Provider>
    );
  }

  return (
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE || undefined,
      }}
      cacheLocation="localstorage"
      useRefreshTokens
      onRedirectCallback={onRedirectCallback}
    >
      <AccessToken />
      {children}
    </Auth0Provider>
  );
};
